import React, { FC } from 'react';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

import { useGetEncountersQuery, useGetResourceQuery } from '../healthWalletSlice';
import type { SectionProps } from './TabRenderer';
import { isVisitUpcoming, isResourceUpcoming, encounterTypeKey, procCategoryKey, ENCOUNTER_TYPE_META, ENCOUNTER_TYPE_ORDER, PROC_CATEGORY_META } from './classify';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { EncountersBody } from './EncountersSections';
import { ProceduresBody, CAT_ORDER } from './ProceduresSections';

// ============================================================================
// Upcoming tab sections — scheduled/future items lifted out of the historical
// tabs. The backend flags future appointments via encounter `isUpcoming`, and
// emits not-yet-performed procedures as ServiceRequests carrying `intent`/`mood`
// (classify.isResourceUpcoming). BOTH slices route here: the visit cards are
// REUSED verbatim via EncountersBody's `upcomingOnly` prop and the procedure
// cards via ProceduresBody's `upcomingOnly` prop (RTK dedupes the shared reads)
// — no duplicated card JSX. Each block is shown only when it has upcoming items.
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };

// Eyebrow label above each stacked block (matches the hero's uppercase accent eyebrow).
const SectionLabel: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase' }}>
      {children}
    </Typography>
  </Container>
);

// --- 1. Hero -----------------------------------------------------------------
export const UpcomingHero: FC<SectionProps> = () => {
  const { data } = useGetEncountersQuery();
  const { data: procData } = useGetResourceQuery('procedures');
  const upcomingVisits = (data ?? []).filter(isVisitUpcoming);
  const upcomingProcs = (procData?.named ?? []).filter((g) => isResourceUpcoming(g, 'procedures'));
  const total = upcomingVisits.length + upcomingProcs.length; // combined: scheduled visits + planned procedures
  // Hero tiles mirror the SAME classifier the reused bodies filter by (present-only) — upcoming visits by
  // encounter TYPE (Office visit, Imaging…) + upcoming procedures by CATEGORY — not a coarse two-block count.
  // Keys prefixed v-/p- so a type and a category that share a name (e.g. "Imaging") don't collide.
  const tiles = [
    ...ENCOUNTER_TYPE_ORDER.map((k) => ({
      key: `v-${k}`, label: ENCOUNTER_TYPE_META[k].label, bg: ENCOUNTER_TYPE_META[k].bg, fg: ENCOUNTER_TYPE_META[k].fg,
      count: upcomingVisits.filter((v) => encounterTypeKey(v) === k).length,
    })),
    ...CAT_ORDER.map((k) => ({
      key: `p-${k}`, label: PROC_CATEGORY_META[k].pill, bg: PROC_CATEGORY_META[k].bg, fg: PROC_CATEGORY_META[k].fg,
      count: upcomingProcs.filter((g) => procCategoryKey(g) === k).length,
    })),
  ].filter((t) => t.count > 0);
  const tile = (value: number, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 'none', width: 46, height: 46, borderRadius: '50%', bgcolor: V1_ACCENT_TINT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EventAvailableIcon sx={{ color: V1_ACCENT }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 0.75 }}>
            Upcoming
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 620 }}>
            {total
              ? <>You have <strong>{total}</strong> upcoming {total === 1 ? 'item' : 'items'} on your record.</>
              : <>No upcoming items are scheduled on your record.</>}
          </Typography>
        </Box>
        {tiles.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {tiles.map((t) => <React.Fragment key={t.key}>{tile(t.count, t.label, t.bg, t.fg)}</React.Fragment>)}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

// --- 2. Body: reuse the Encounters + Procedures cards, filtered to upcoming ----
export const UpcomingBody: FC<SectionProps> = (ctx) => {
  const { data, isLoading } = useGetEncountersQuery();
  const { data: procData, isLoading: isProcLoading } = useGetResourceQuery('procedures');
  if (isLoading || isProcLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const hasUpcomingVisits = (data ?? []).some(isVisitUpcoming);
  const hasUpcomingProcs = (procData?.named ?? []).some((g) => isResourceUpcoming(g, 'procedures'));
  if (!hasUpcomingVisits && !hasUpcomingProcs) {
    return (
      <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
        <Paper elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', p: '44px 26px', textAlign: 'center' }}>
          <Box sx={{ width: 54, height: 54, borderRadius: '50%', bgcolor: V1_ACCENT_TINT, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <EventAvailableIcon sx={{ color: V1_ACCENT, fontSize: 27 }} />
          </Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, mb: 0.75 }}>No upcoming items</Typography>
          <Typography sx={{ fontSize: 14, color: '#6b6b70', lineHeight: 1.5, maxWidth: 470, mx: 'auto' }}>
            When your providers schedule a future appointment or order a procedure, it will show up here. Your past visits and
            performed procedures stay in the Encounters and Procedures tabs.
          </Typography>
        </Paper>
      </Container>
    );
  }
  return (
    <>
      {/* Reuse each body verbatim via its upcomingOnly prop; show a block only when it has items. */}
      {hasUpcomingVisits && (
        <>
          <SectionLabel>Appointments</SectionLabel>
          <EncountersBody {...ctx} upcomingOnly />
        </>
      )}
      {hasUpcomingProcs && (
        <>
          <SectionLabel>Procedures</SectionLabel>
          <ProceduresBody {...ctx} upcomingOnly />
        </>
      )}
    </>
  );
};
